import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useScrollReveal } from "../../../hooks/useScrollReveal";
import { useNavigationContext } from "../../common/navigation/NavigationContext";
import { executeWorkflowHandshake } from "./Workflow-esg/Workflow_XLS_Support";

export default function WorkflowRefreshESG() {
  const navigate = useNavigate();
  const revealRef = useScrollReveal();
  const { setWorkflowContext } = useNavigationContext();

  const [hasCleanInventory, setHasCleanInventory] = useState(false);

  useEffect(() => {
    setWorkflowContext(
      "Refresh ESG Taxonomy",
      "/registered/site-admin/taxonomy-workflow",
    );

    const checkInventory = async () => {
      console.group("📡 [REFRESH_DIAGNOSTIC]: S3 Clean Room Inventory Check");

      const payload = {
        action: "GET_INVENTORY",
        user_id: sessionStorage.getItem("SEC_USER_GUID") || "SYSTEM_USER",
        payload: {
          bucket: "esg-staging-clean",
          prefix: "",
        },
      };

      try {
        console.log("➡️ Workflow URL Base:", "https://localhost:49475");
        console.log("➡️ Route:", "doorman/inventory");
        console.log("➡️ Intent:", "Inventory");
        console.log("➡️ Payload Sent To BFF:", payload);
        console.log(
          "➡️ Payload JSON Sent To BFF:",
          JSON.stringify(payload, null, 2),
        );

        const response = await executeWorkflowHandshake(
          "doorman/inventory",
          payload,
          "Inventory",
        );

        console.log("⬅️ Raw BFF Response:", response);
        console.log(
          "⬅️ Raw BFF Response JSON:",
          JSON.stringify(response, null, 2),
        );

        const files = response?.data?.files || [];

        console.log("📁 Extracted Files:", files);
        console.log("📊 Extracted File Count:", files.length);

        if (files.length > 0) {
          console.log("✅ Inventory present. Template step enabled.");
          setHasCleanInventory(true);
        } else {
          console.warn(
            "⚠️ No clean inventory returned. Template step remains disabled.",
          );
          setHasCleanInventory(false);
        }
      } catch (err) {
        console.error("🚨 FAIL: Inventory handshake failed.", err);
        console.error("🚨 FAIL MESSAGE:", err?.message);
        setHasCleanInventory(false);
      } finally {
        console.groupEnd();
      }
    };

    checkInventory();
  }, [setWorkflowContext]);

  const workflowSteps = [
    {
      title: "Import ESG",
      description:
        "Upload official EFRAG Excel taxonomy linkbases (Presentation, Calculation, and Definition).",
      icon: "bi-file-earmark-excel-fill",
      actionLabel: "Upload ESG Taxonomy",
      actionTarget: "/registered/upload/monitor",
      active: true,
    },
    {
      title: "Validate and Prepare",
      description:
        "Choose the file uploaded in step 1 to build the declaration template for an ESG reporting period.",
      icon: "bi-diagram-3-fill",
      actionLabel: "Process Data",
      actionTarget: "/registered/site-admin/workflow_hub",
      active: true,
    },
    {
      title: "Reporting Templates",
      description:
        "Curate specific disclosure scopes using the generated taxonomy logic.",
      icon: "bi-collection-fill",
      actionLabel: "Manage Templates",
      actionTarget: "/registered/site-admin/workflow/template-builder/new",
      active: false, // Template Builder hidden 2026-10-03
    },
    {
      title: "Evaluation & Determination",
      description:
        "Capture inputs against templates to determine compliance and materiality scores.",
      icon: "bi-check-circle-fill",
      actionLabel: "Open Evaluator",
      actionTarget: "#",
      active: false,
    },
  ];

  return (
    <div className="page-main" data-scroll-container>
      <div className="page-main-inner">
        <section ref={revealRef} className="page-hero reveal-section">
          <h1 className="page-hero__title">Refresh ESG Taxonomy</h1>
          <p className="page-hero__lead">
            Strategic Workflow: Transforming EFRAG Source into Reporting
            Intelligence
          </p>
        </section>

        <section className="page-section px-4">
          <div className="alert alert-warning d-flex align-items-start gap-2 mb-4" role="alert">
            <i className="bi bi-exclamation-triangle-fill fs-5 mt-1"></i>
            <div>
              <strong>Old POC &mdash; no longer valid.</strong> This workflow (Excel-based
              taxonomy import and spreadsheet reconstruction) was the original
              proof-of-concept and is superseded by the new XBRL Processor. It remains
              available only as a safety net until the new version has been fully tested.
              See <strong>XBRL Processor</strong> on the Admin Dashboard for the current
              path.
            </div>
          </div>

          <div className="row g-4 mb-5">
            {workflowSteps.map((step, index) => (
              <div key={index} className="col-12 col-md-6 col-lg-3">
                <div
                  className={`card h-100 border-0 shadow-sm bg-white ${!step.active ? "opacity-50" : ""}`}
                >
                  <div className="card-body p-4 d-flex flex-column text-center">
                    <div className="d-inline-flex align-items-center justify-content-center bg-primary bg-opacity-10 text-primary rounded-circle mb-3 p-4 mx-auto">
                      <i className={`bi ${step.icon} fs-2`}></i>
                    </div>
                    <h5 className="fw-bold mb-2 small text-uppercase text-secondary">
                      Step {index + 1}
                    </h5>
                    <h6 className="fw-bold mb-3">{step.title}</h6>
                    <p className="small text-muted mb-4 flex-grow-1">
                      {step.description}
                    </p>

                    <button
                      type="button"
                      className={`btn w-100 fw-bold ${step.active ? "btn-primary shadow-sm" : "btn-outline-secondary disabled"}`}
                      disabled={!step.active}
                      onClick={() => step.active && navigate(step.actionTarget)}
                    >
                      {step.actionLabel}
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-5 pt-4 border-top d-flex justify-content-between">
            <button
              type="button"
              className="btn btn-link text-decoration-none p-0 text-secondary small"
              onClick={() => navigate("/registered/site-admin")}
            >
              <i className="bi bi-arrow-left me-2"></i>
              Return to Admin Dashboard
            </button>
            <span className="text-muted small align-self-center">
              Inventory Status:{" "}
              <strong>
                {hasCleanInventory
                  ? "Validated Data Available"
                  : "Awaiting Taxonomy Ingestion"}
              </strong>
            </span>
          </div>
        </section>
      </div>
    </div>
  );
}